import { useFormContext, Controller } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import { FileUploadInput } from "@/components/FileUploadInput";
import { useFreelancerStore } from "@/store/freelancerStore";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";
import { CATEGORIES } from "@/utils/constant";
import "react-phone-number-input/style.css";

interface FileUploadInputProps {
  setIsLoading?: (isLoading: boolean) => void;
}

export const FreelancerPersonalForm = ({
  setIsLoading,
}: FileUploadInputProps) => {
  const {
    register,
    setValue,
    formState: { errors },
    control,
    watch,
  } = useFormContext();
  const { setPartialProfile } = useFreelancerStore();
  const { toast } = useToast();
  const { user } = useUserStore();
  const { progress, uploadFreelancerResume } = useFreelancerStore();

  const uploadResumeAPI = async (file: File): Promise<any> => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("resume", file);
    const resumeError = errors?.resume_url?.message;

    if (resumeError) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await uploadFreelancerResume(
        user?.userId || "",
        user?.role || "",
        user?.accessToken || "",
        formData
      );
      setIsLoading(false);
      return res.data;
    } catch (error) {
      setIsLoading(false);
      toast({
        title: "Error",
        description: "Failed to upload resume. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <FileUploadInput
        name="resume_url"
        buttonText="Upload resume"
        tooltipText="Upload your resume (PDF/DOC, max 5MB)."
        label="Upload your resume for AI-driven auto-fill of form fields"
        value={watch("resume_url")}
        onChange={(file) => setValue("resume_url", file)}
        onUpload={uploadResumeAPI}
        progress={progress}
        onUploadSuccess={(response) => {
          const freelancer = response?.freelancer;
          if (freelancer) {
            setValue("userId", freelancer.userId || "");
            setValue("resume_url", freelancer.resume_url || "");
            setValue("phone", freelancer.phone || "");
            setValue("designation", freelancer.designation || "");
            setValue(
              "category",
              CATEGORIES.find(
                (cat) =>
                  cat.toLowerCase().replace(/[^a-z0-9]/g, '') ===
                  (freelancer.category || "").toLowerCase().replace(/[^a-z0-9]/g, '')
              ) || "Other"
            );
            setValue(
              "experience",
              (freelancer.experience &&
                freelancer.experience.split(/[^\d]/)[0]) ||
              0
            );
            setValue("skills", freelancer.skills || []);
            setValue("certifications", freelancer.certifications || []);
            setValue("bio", freelancer.bio || "");
            setValue("workExperience", freelancer.workExperience || []);
            setValue("projectsCompleted", freelancer.projectsCompleted || 0);
            setValue("dailyRate", freelancer.dailyRate || 1);
            setValue("location", freelancer.location || "");
            setValue("achievements", freelancer.achievements || []);
            setValue("name", freelancer.fullName || "");
            setValue("education", freelancer?.education || []);
            setValue("projects", freelancer?.project || []);

            if (freelancer?.socialMediaLinks) {
              setValue("socialMediaLinks", freelancer?.socialMediaLinks || []);
              setValue(
                "socialMediaLinksArray",
                (freelancer?.socialMediaLinks || []).map((url) => ({
                  value: url,
                }))
              );
            }

            setPartialProfile({
              userId: freelancer?.userId,
              name: freelancer?.fullName || "",
              experience: freelancer?.experience,
              phone: freelancer?.phone,
              designation: freelancer?.designation,
              rating: freelancer?.rating,
              skills: freelancer?.skills,
              category: freelancer?.category,
              projectsCompleted: freelancer?.projectsCompleted,
              resume_url: freelancer?.resume_url,
              certifications: freelancer?.certifications,
              achievements: freelancer?.achievements || [],
              dailyRate: freelancer?.dailyRate,
              totalEarnings: freelancer?.totalEarnings,
              bio: freelancer?.bio,
              availability: freelancer?.availability,
              socialMediaLinks: freelancer?.socialMediaLinks,
              location: freelancer?.location,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });

            toast({
              title: "Resume uploaded",
              description:
                "Resume parsed successfully and form fields updated.",
              variant: "default",
            });
          }
        }}
        toast={toast}
        error={errors.resume_url?.message as string}
      />

      <div className="space-y-2">
        <div className="flex items-center">
          <Label
            htmlFor="location"
            className="font-medium text-sm text-gray-700"
          >
            Location <span className="text-red-600">*</span>
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Your city and country (e.g., New York, USA).
            </div>
          </div>
        </div>
        <Input
          id="location"
          placeholder="Enter your location"
          {...register("location")}
          className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.location && (
          <p className="text-xs text-red-500">
            {errors.location.message as string}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone"  className="font-medium text-sm text-gray-700">Contact Phone <span className="text-red-600">*</span></Label>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <PhoneInput
              {...field}
              international
              defaultCountry="US"
              placeholder="Enter phone number"
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300 rounded-md border p-2 w-full [&_input]:bg-transparent"
            />
          )}
        />
        {errors.phone?.message && (
          <p className="text-xs text-red-500">
            {errors.phone.message as string}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label htmlFor="bio" className="font-medium text-sm text-gray-700">
            Bio <span className="text-red-600">*</span>
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              A brief introduction about yourself and your expertise.
            </div>
          </div>
        </div>
        <Textarea
          id="bio"
          placeholder="Tell us about yourself..."
          {...register("bio")}
          className="min-h-[150px] border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.bio && (
          <p className="text-xs text-red-500">{errors.bio.message as string}</p>
        )}
      </div>
    </div>
  );
};
